// Copyright (c) Docugami, Inc. All rights reserved.

import { NotificationModel } from "networking/NotificationsApi";
import { NotificationsManagerSingleton } from "notifications/NotificationsManager";
import { useCallback, useEffect, useRef, useState } from "react";
import { INotificationDescriptor, INotificationProvider, NotificationProviderType } from "notifications/INotificationProvider";
import { NotificationComponentFactory } from "components/notifications/NotificationComponentFactory";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { limitedNotificationTypes } from "components/common/LimitsConstants";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";

/**
 * Provides notifications from the service to be rendered in the UI.
 */
export const useServiceNotificationProvider = (): INotificationProvider => {
    const [descriptors, setDescriptors] = useState<INotificationDescriptor[]>(generateDescriptors(NotificationsManagerSingleton.notifications));
    const factoryRef = useRef(new NotificationComponentFactory());
    const isLimited = useSelector((state: AppState) => selectIsLimitedOrganization(state));

    const createComponent = useCallback((descriptor: INotificationDescriptor) => {
        if (descriptor.providerType !== NotificationProviderType.Service) {
            throw new Error(`Service notification provider is incompatible with provider type ${descriptor.providerType}.`);
        }

        const notificationModel = NotificationsManagerSingleton.getNotification(descriptor.id);
        if (!notificationModel) {
            console.error(`Could not generate a notification for the given ID: ${descriptor.id}`)
            return <></>;
        }

        if (isLimited && limitedNotificationTypes.includes(notificationModel.notificationType)) {
            return null;
        }

        return factoryRef.current.createNotificationComponent(notificationModel);
    }, [isLimited]);

    useEffect(() => {
        function onNotificationsChange() {
            // We can be smarter here in the future since we know about adds, removes, and modifies, but for now any change is going to trigger
            // us to re-generate our descriptors which in turn will update the UX. Given that we don't expect there to ever be hundreds of
            // notifications this should be alright.
            const newDescriptors = generateDescriptors(NotificationsManagerSingleton.notifications);
            setDescriptors(newDescriptors);
        }

        const unregister = NotificationsManagerSingleton.observableNotifications.registerListener(onNotificationsChange)
        return () => unregister();
    },[]);

    return {
        descriptors: descriptors,
        createComponent: createComponent
    };
}

function generateDescriptors(items: NotificationModel[]): INotificationDescriptor[] {
    return items.map(model => {
        const descriptor: INotificationDescriptor = {
            providerType: NotificationProviderType.Service,
            id: model.id,
            category: model.category,
            timestamp: new Date(model.createdAt),
            interactions: model.interactions
        };

        return descriptor;
    });
}
